/**
 * Lightweight product analytics — fire-and-forget event tracking.
 * Writes to the `analytics_events` table on Lovable Cloud.
 *
 * Privacy:
 * - Never store free-text user input (AI prompts must be hashed before passing).
 * - No PII fields (email, name, phone) — sanitized at insert time.
 * - Failures are swallowed; analytics MUST never break the UI.
 */
import { cloudClient as supabase } from '@/integrations/supabase/cloudClient';

const SESSION_KEY = 'ledlogic_analytics_session_v1';

function getSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return `s_${Date.now().toString(36)}`;
  }
}

const PII_KEYS = /^(email|name|full_name|phone|address|password|token|secret)$/i;

function sanitize(props: Record<string, unknown> = {}): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(props)) {
    if (PII_KEYS.test(k)) continue;
    if (v === null || v === undefined) continue;
    if (typeof v === 'string') {
      out[k] = v.length > 200 ? v.slice(0, 200) : v;
    } else if (typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v;
    } else {
      try { out[k] = JSON.parse(JSON.stringify(v)); } catch { /* drop */ }
    }
  }
  return out;
}

/** SHA-256 hash for free-text fields (e.g. AI prompts). Returns hex string. */
export async function hashText(text: string): Promise<string> {
  try {
    const buf = new TextEncoder().encode(text);
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(digest))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return 'hash_unavailable';
  }
}

/** The 12 canonical product events from the MVP feedback report. */
export type AnalyticsEvent =
  | 'home_viewed'
  | 'brand_selected'
  | 'series_selected'
  | 'panel_viewed'
  | 'configurator_opened'
  | 'configurator_tab_switched'
  | 'configuration_saved'
  | 'compare_opened'
  | 'compare_viewed'
  | 'ai_assistant_query_sent'
  | 'pdf_downloaded'
  | 'issue_reported'
  | 'session_extended'
  | 'admin_page_viewed'
  | 'cookie_consent_recorded'
  | 'diagram_render_failed'
  | 'diagram_perf_sample';

export async function track(event: AnalyticsEvent, props: Record<string, unknown> = {}): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const row = {
      event_name: event,
      props: sanitize(props) as never,
      session_id: getSessionId(),
      user_id: user?.id ?? null,
    };
    // Fire-and-forget: don't await the network response in calling code paths.
    void supabase.from('analytics_events').insert([row]);
  } catch {
    // analytics failures must never break the app
  }
}
